/**
 * klubb-header-theme — a club's own colours and logo in the header (D74, 02.11.2026).
 *
 * A bigger club can set four optional fields on its Club entry: the header's background (one colour for
 * both bands), the background of the «Din klubb» box, a text colour for the menu items and the club
 * page's breadcrumb, and a header logo that replaces the klubb.no logo (made as klubb.no + the club's
 * logo). Text, icons and the logo's letters take the opposite colour of the background (black or white,
 * whichever reads best), and so does the text in the club box. A blank field keeps the stock header.
 *
 * The page's club is one of two (the same rule as the chip): the club the URL names (a club catalogue and
 * the products under it, data-klubb-url-club), else the member's club (window.klubb.activeClub, which is
 * known at once from the stored copy; rule 4: Liquid cannot read the cookie). So the look is drawn in the
 * browser on every page, from the club record (snippets/klubb-club-json.liquid), and there is no second,
 * server-side version of it.
 *
 * Why its own script, loaded in <head> right after klubb-club-state.js: the colours are CSS custom
 * properties on <html>, set before the first paint, so a member never sees the black header turn into the
 * club's. The logo needs the header, so the header section calls apply() again right after the logo link.
 * klubb-header-hydrate.js runs later (deferred) and is not involved.
 *
 * The CSS is in klubb-header.css (--klubb-header-bg/-fg, --klubb-header-text, --klubb-header-box-bg/-fg;
 * each use has the stock colour as its fallback) and, for the breadcrumb, klubb-plp.css. Display only:
 * nothing here decides a price or an order (rule 13).
 */

(function (root) {
  'use strict';

  const WHITE = '#ffffff';
  const BLACK = '#0a0a0a'; // --klubb-black. Keep in step with klubb-tokens.css.

  // The CSS custom properties this script owns (all on <html>).
  const VAR_BG = '--klubb-header-bg';         // header_background: both bands
  const VAR_FG = '--klubb-header-fg';         // the opposite colour of it: icons, B2B link, the logo's letters
  const VAR_TEXT = '--klubb-header-text';     // header_text_color: menu items + the club page's breadcrumb, only when chosen
  const VAR_BOX_BG = '--klubb-header-box-bg'; // header_club_box_background: the «Din klubb» box
  const VAR_BOX_FG = '--klubb-header-box-fg'; // the text in the box
  const VAR_NAMES = [VAR_BG, VAR_FG, VAR_TEXT, VAR_BOX_BG, VAR_BOX_FG];

  // State classes on <html> that the CSS gates on. The hover tints are made from the text colour, so they must
  // only apply when the header has a colour; the breadcrumb must only follow a text colour that was chosen
  // (it sits on the club hero, not on the header, so the automatic colour is not for it).
  const CLASS_THEMED = 'klubb-header-themed';
  const CLASS_TEXT = 'klubb-header-text-themed';

  // --- Colour ---

  // '#abc' / '#aabbcc' (any case) -> '#aabbcc', else null. Colour fields always give '#rrggbb'; anything else
  // (blank, a name, a stored copy gone wrong) means «not set».
  function normalizeHex(value) {
    const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(value == null ? '' : value).trim());
    if (!m) return null;
    const hex = m[1].length === 3 ? m[1].replace(/./g, '$&$&') : m[1];
    return '#' + hex.toLowerCase();
  }

  // WCAG 2.x relative luminance of '#rrggbb'.
  function luminance(hex) {
    const rgb = [1, 3, 5].map(function (i) {
      const s = parseInt(hex.slice(i, i + 2), 16) / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
  }

  function contrast(a, b) {
    const hi = Math.max(a, b);
    const lo = Math.min(a, b);
    return (hi + 0.05) / (lo + 0.05);
  }

  // The text colour for a background: white or black, whichever has the higher contrast. null when the
  // background is not a colour.
  function readableOn(background) {
    const hex = normalizeHex(background);
    if (!hex) return null;
    const l = luminance(hex);
    return contrast(l, luminance(WHITE)) >= contrast(l, luminance(BLACK)) ? WHITE : BLACK;
  }

  // The CSS custom properties for a club record: { '--klubb-header-bg': '#rrggbb', '--klubb-header-fg': '#ffffff', … }.
  // A field that is not a colour sets nothing, so that part keeps the stock header.
  function varsFor(club) {
    const vars = {};
    if (!club) return vars;
    const bg = normalizeHex(club.header_background);
    if (bg) {
      vars[VAR_BG] = bg;
      vars[VAR_FG] = readableOn(bg);
    }
    const text = normalizeHex(club.header_text_color);
    if (text) vars[VAR_TEXT] = text;
    const box = normalizeHex(club.header_club_box_background);
    if (box) {
      vars[VAR_BOX_BG] = box;
      vars[VAR_BOX_FG] = readableOn(box);
    }
    return vars;
  }

  // The header logo's address, or '' (a record without one, or an address that is not http(s)).
  function logoUrlFor(club) {
    const url = club && club.header_logo_url;
    return typeof url === 'string' && /^(https?:)?\/\//.test(url) ? url : '';
  }

  // --- The page ---

  // The club this page is drawn for: the URL's club wins over the member's (the page is that club's page,
  // as its hero and chip are), else the member's own.
  function pageClub(k, doc) {
    const urlSlug = doc.documentElement.getAttribute('data-klubb-url-club');
    return (urlSlug ? k.clubBySlug(urlSlug) : k.activeClub) || null;
  }

  function applyColours(doc, club) {
    const html = doc.documentElement;
    const vars = varsFor(club);
    VAR_NAMES.forEach(function (name) {
      if (vars[name]) html.style.setProperty(name, vars[name]);
      else html.style.removeProperty(name);
    });
    html.classList.toggle(CLASS_THEMED, !!vars[VAR_BG]);
    html.classList.toggle(CLASS_TEXT, !!vars[VAR_TEXT]);
  }

  // The club logo goes next to the klubb.no logo, which is hidden by CSS while it is there (the modifier on the
  // link), so removing it gives the klubb.no logo back untouched. Idempotent: a call with the same club does nothing.
  function applyLogo(doc, club) {
    const link = doc.querySelector('[data-klubb-header-logo]');
    if (!link) return;
    const url = logoUrlFor(club);
    const current = link.querySelector('[data-klubb-club-logo]');
    if (!url) {
      if (current) current.remove();
      link.classList.remove('klubb-header__logo-link--club');
      return;
    }
    if (current && current.getAttribute('src') === url && current.getAttribute('data-klubb-club-logo') === club.slug) return;
    if (current) current.remove();
    const img = doc.createElement('img');
    img.className = 'klubb-header__logo-club';
    img.setAttribute('data-klubb-club-logo', club.slug);
    img.setAttribute('src', url);
    img.setAttribute('alt', String(link.getAttribute('data-klubb-logo-alt') || '').replace('%CLUB%', club.name || ''));
    // The width/height attributes only give the browser the shape, so the header does not move when the file
    // arrives (the CSS sets the height).
    const ratio = Number(club.header_logo_ratio) || 0;
    if (ratio > 0) {
      img.setAttribute('width', String(Math.round(ratio * 100)));
      img.setAttribute('height', '100');
    }
    link.appendChild(img);
    link.classList.add('klubb-header__logo-link--club');
  }

  // Draws the header for the page's club (or the stock header when there is none). Safe to call at any time and
  // as often as you like; it never throws, because a header look must not get in the way of a paying customer.
  function apply() {
    try {
      const k = root.klubb;
      const doc = root.document;
      if (!k || !doc || !doc.documentElement) return;
      const club = pageClub(k, doc);
      applyColours(doc, club);
      applyLogo(doc, club);
    } catch (e) { /* keep the header as it is */ }
  }

  root.klubbHeaderTheme = { apply: apply, varsFor: varsFor, readableOn: readableOn, logoUrlFor: logoUrlFor };

  if (typeof document !== 'undefined' && root.klubb) {
    apply();
    // First visit without a stored copy: the club arrives after the lookup. A club switch navigates, so this is the only change.
    if (typeof root.klubb.on === 'function') root.klubb.on('change', apply);
    // The logo needs the header: again once it is parsed, when the theme editor redraws the header section,
    // and when the browser restores the page from its back/forward cache (the club may have changed meanwhile).
    document.addEventListener('DOMContentLoaded', apply);
    document.addEventListener('shopify:section:load', apply);
    if (typeof root.addEventListener === 'function') {
      root.addEventListener('pageshow', function (e) { if (e.persisted) apply(); });
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
